import React, { useState, useContext, useEffect } from "react";
import styles from "./BcsConfigurationModal.module.css";
import Button from '@asphalt-react/button';
import Dropdown from '@asphalt-react/dropdown';
import { Cross } from "@asphalt-react/iconpack";
import ToggleSwitch from "@asphalt-react/toggle-switch";
import Loader from "../../common/components/Loader";
import { updateBcsAppIdValues, getBcsMobileConfig, checkVpnConnection } from "../api/illustrationsApi";
import { getAppIds } from "../api/appIdsApi";
import { UserContext } from "../../common/hooks/UserContext";

const VPN_ENVIRONMENT = process.env.REACT_APP_VPN_ENVIRONMENT;
const APP_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
const IS_INTEGRATION = APP_ENVIRONMENT === 'integration';
const IS_PRODUCTION = APP_ENVIRONMENT === 'prod';
// Staging: Integration + QA. Production Coffer: Integration + QA + Production.
const SHOW_BCS_ENV_SELECTOR = IS_INTEGRATION || IS_PRODUCTION;

const bcsEnvItems = IS_PRODUCTION
    ? [
          { id: '0', key: 'Integration', identifier: 'integration' },
          { id: '1', key: 'QA',          identifier: 'qa' },
          { id: '2', key: 'Production',  identifier: 'prod' },
      ]
    : [
          { id: '0', key: 'Integration', identifier: 'integration' },
          { id: '1', key: 'QA',          identifier: 'qa' },
      ];

// Sent as x-coffer-bcs-env for int/QA routing on the BFF. Prod omits the header
// so the BFF uses its default BCS_HOST_URL / token.
const BCS_ENV_HEADER = {
    integration: 'integration',
    qa: 'qa',
    prod: undefined,
};

function getBffUrl(env) {
    if (env === 'prod') {
        return process.env.REACT_APP_BFF_BASE_URL;
    }
    // Integration + QA go through the integration BFF (QA uses X-Coffer-BCS-Env: qa).
    if (env === 'qa') {
        return (
            process.env.REACT_APP_QA_BFF_BASE_URL ||
            process.env.REACT_APP_INT_BFF_BASE_URL ||
            process.env.REACT_APP_BFF_BASE_URL
        );
    }
    return process.env.REACT_APP_INT_BFF_BASE_URL || process.env.REACT_APP_BFF_BASE_URL;
}

const texts = {
    title: "BCS Config",
    description: "Enable or disable required App IDs for this asset.",
    cancelButton: "Cancel",
    saveButton: "Save Configuration",
    saveFailedMessage: "Could not save the configuration. Please try again.",
    loadFailedMessage: "Could not load the BCS configuration. Please close and try again.",
    vpnRequiredMessage: `Connect to the ${VPN_ENVIRONMENT} VPN to load the BCS configuration.`
};

const getFinalAppIdsWithStatus = (firebaseAppIds, bcsConditions) => {
    return firebaseAppIds.map((appId) => {
        const condition = bcsConditions.find(
            (bcsCondition) => bcsCondition.applicationId === appId
        );

        let illustrationUrl = "";
        if (condition) {
            try {
                illustrationUrl =
                    JSON.parse(condition.value)?.illustration_url ?? "";
            } catch (error) {
                console.error(
                    "Unparseable BCS condition value:",
                    condition.value
                );
            }
        }

        return {
            name: appId,
            enabled: illustrationUrl.length > 0,
        };
    });
};

function resolveAppIdsForEnv(firebaseAppIds, bcsEnv) {
    if (bcsEnv === 'prod') {
        return firebaseAppIds.prod || firebaseAppIds.production || [];
    }
    return firebaseAppIds[bcsEnv] || [];
}

function BcsConfigurationModal({ combinedName, darkroomUrl, darkroomUrlDark, onClose }) {
    const { user } = useContext(UserContext);

    // Default to prod on production Coffer so saves don't accidentally hit int/QA.
    const [bcsEnv, setBcsEnv] = useState(IS_PRODUCTION ? 'prod' : 'integration');

    const [initialAppIds, setInitialAppIds] = useState([]);
    const [finalAppIds, setFinalAppIds] = useState([]);

    const [isLoadingConfig, setIsLoadingConfig] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const bcsEnvHeader = SHOW_BCS_ENV_SELECTOR ? BCS_ENV_HEADER[bcsEnv] : undefined;
    const bffUrl = SHOW_BCS_ENV_SELECTOR ? getBffUrl(bcsEnv) : process.env.REACT_APP_BFF_BASE_URL;
    const initialEnvItem =
        bcsEnvItems.find((item) => item.identifier === bcsEnv) || bcsEnvItems[0];

    useEffect(() => {
        setIsLoadingConfig(true);
        setLoadError(null);
        setFinalAppIds([]);
        setInitialAppIds([]);

        checkVpnConnection().then((isConnected) => {
            if (!isConnected) {
                setLoadError(texts.vpnRequiredMessage);
                setIsLoadingConfig(false);
                return;
            }
            Promise.all([
                getAppIds(),
                getBcsMobileConfig(combinedName.toUpperCase(), user.idToken, bffUrl, bcsEnvHeader),
            ])
                .then(([firebaseAppIds, response]) => {
                    const conditions =
                        response.data.data?.global?.conditions ?? [];
                    const merged = getFinalAppIdsWithStatus(
                        resolveAppIdsForEnv(firebaseAppIds, bcsEnv),
                        conditions
                    );
                    setInitialAppIds(merged);
                    setFinalAppIds(merged);
                    setIsLoadingConfig(false);
                })
                .catch((error) => {
                    console.error(error);
                    setLoadError(texts.loadFailedMessage);
                    setIsLoadingConfig(false);
                });
        });
    }, [combinedName, user, bcsEnv, bffUrl, bcsEnvHeader]);

    const handleToggleAppId = (name, on) => {
        setFinalAppIds(
            finalAppIds.map((appId) =>
                appId.name === name ? { ...appId, enabled: on } : appId
            )
        );
    };

    const handleSaveClick = async () => {
        const changed = finalAppIds.filter((appId) => {
            const initial = initialAppIds.find((i) => i.name === appId.name);
            return !initial || initial.enabled !== appId.enabled;
        });

        if (changed.length === 0) {
            onClose();
            return;
        }

        const keys = changed.map((appId) => ({
            configKey: combinedName.toUpperCase(),
            configValue: appId.enabled
                ? JSON.stringify({ illustration_url: darkroomUrl, illustration_url_dark: darkroomUrlDark ?? darkroomUrl })
                : JSON.stringify({}),
            dimensions: {
                app_id: appId.name,
                platform: "mobile",
            },
        }));

        setIsSaving(true);
        setSaveError(null);
        try {
            await updateBcsAppIdValues(keys, user.idToken, bffUrl, bcsEnvHeader);
            onClose();
        } catch (error) {
            console.error(error);
            setSaveError(texts.saveFailedMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div>
                        <h2 className={styles.title}>{texts.title}</h2>
                        <p className={styles.assetName}>{combinedName}</p>
                    </div>
                    <Button nude qualifier={<Cross />} onClick={onClose} />
                </div>

                {SHOW_BCS_ENV_SELECTOR && (
                    <div className={styles.envSelector}>
                        <Dropdown
                            items={bcsEnvItems}
                            initialSelectedItem={initialEnvItem}
                            onChange={(item) => setBcsEnv(item.identifier)}
                        />
                    </div>
                )}

                <p className={styles.description}>{texts.description}</p>

                {isLoadingConfig ? (
                    <div className={styles.loaderContainer}>
                        <Loader />
                    </div>
                ) : (
                    finalAppIds.length > 0 && (
                        <div className={styles.appIdList}>
                            {finalAppIds.map((appId) => (
                                <div className={styles.appIdRow} key={appId.name}>
                                    <p className={styles.appIdName}>{appId.name}</p>
                                    <div className={styles.rowActions}>
                                        <ToggleSwitch
                                            on={appId.enabled}
                                            onToggle={({ on }) =>
                                                handleToggleAppId(appId.name, on)
                                            }
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}

                {loadError && <p className={styles.saveError}>{loadError}</p>}
                {saveError && <p className={styles.saveError}>{saveError}</p>}

                <div className={styles.footer}>
                    <Button nude onClick={onClose}>
                        {texts.cancelButton}
                    </Button>
                    <Button
                        primary
                        onClick={handleSaveClick}
                        disabled={isSaving || isLoadingConfig || !!loadError}>
                        {isSaving ? "Saving..." : texts.saveButton}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default BcsConfigurationModal;
